import { useCallback, useState } from 'react';
import { commsApi } from '@/utils/commsApi';
import { useVoiceRecorder, extForMime } from '@/hooks/useVoiceRecorder';

export const VOICE_TASK_MAX_SECONDS = 120;
const newDraftId = () => `d-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// Editable task drafts produced by the AI from a recording or any text (chat answers included).
export function useTaskDrafts({ teamId, onError, descriptionSuffix = '' }) {
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [drafts, setDrafts] = useState([]);

  const fail = useCallback((message) => onError?.(message), [onError]);

  const requestDrafts = useCallback(async ({ file, text }) => {
    if (!teamId) { fail('Pick a team first'); return false; }
    setProcessing(true);
    try {
      const data = await commsApi.voiceTaskDrafts(teamId, { file, transcript: text, language: navigator.language?.slice(0, 2) });
      setTranscript(data.transcript);
      setDrafts(data.tasks.map((t) => ({
        ...t,
        _id: newDraftId(),
        team_id: data.team_id,
        description: descriptionSuffix ? `${(t.description || '').trim()}\n\n${descriptionSuffix}`.trim() : t.description,
        assigned_to: t.suggested_assignee || '',
        estimated_hours: t.estimated_hours ?? '',
        due_date: t.due_date || '',
      })));
      return true;
    } catch (err) {
      fail(err?.response?.data?.detail || 'Could not turn that into tasks');
      return false;
    } finally {
      setProcessing(false);
    }
  }, [teamId, fail, descriptionSuffix]);

  const updateDraft = useCallback((id, patch) => setDrafts((prev) => prev.map((d) => (d._id === id ? { ...d, ...patch } : d))), []);
  const removeDraft = useCallback((id) => setDrafts((prev) => prev.filter((d) => d._id !== id)), []);
  const clear = useCallback(() => { setDrafts([]); setTranscript(''); }, []);

  return { processing, transcript, drafts, requestDrafts, updateDraft, removeDraft, clear };
}

// Tap-to-record (≤2 min) → whisper-1 → 1-5 editable task drafts for the selected team.
export function useVoiceTaskDrafts({ teamId, onError }) {
  const base = useTaskDrafts({ teamId, onError });
  const { requestDrafts } = base;
  const fail = useCallback((message) => onError?.(message), [onError]);

  const recorder = useVoiceRecorder({
    maxSeconds: VOICE_TASK_MAX_SECONDS,
    onClip: ({ blob, mimeType }) => requestDrafts({
      file: new File([blob], `voice-task.${extForMime(mimeType)}`, { type: mimeType.split(';')[0] }),
    }),
    onError: (err) => {
      if (err?.message === 'too_short') fail('Keep recording a little longer');
      else if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') fail('Microphone access was denied. Allow it in your browser settings to capture tasks by voice.');
      else if (err?.message === 'unsupported') fail('Voice recording is not supported in this browser');
      else fail('Could not start recording');
    },
  });

  const toggle = useCallback(() => (recorder.isRecording ? recorder.stop() : recorder.start()), [recorder]);

  return {
    ...base,
    isRecording: recorder.isRecording,
    elapsed: recorder.elapsed,
    permission: recorder.permission,
    maxSeconds: VOICE_TASK_MAX_SECONDS,
    toggle,
  };
}
